import { useCallback, useEffect, useRef, useState } from "react"

import { toast } from "sonner"

import { createProject, deleteProject, getProject, getProjects, updateProject } from "@/lib/api"
import type { Project, ProjectDetail } from "@/types/api"

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      const result = await getProjects()
      if (mounted.current) setProjects(result)
    } catch {
      if (mounted.current) toast.error("Projects could not be loaded. Please try again.")
    } finally {
      if (mounted.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const create = useCallback(async (name: string, description: string) => {
    if (projects.length >= 100) throw new Error("You can create up to 100 projects.")
    const project = await createProject({ name, description })
    setProjects((current) => [project, ...current])
    return project
  }, [projects.length])

  const update = useCallback(async (id: string, body: { name?: string; description?: string; instructions?: string }) => {
    if (body.name !== undefined && (body.name.trim().length < 1 || body.name.trim().length > 80)) {
      throw new Error("Project names must be between 1 and 80 characters.")
    }
    if (body.instructions !== undefined && body.instructions.length > 8000) {
      throw new Error("Project instructions must be 8,000 characters or fewer.")
    }
    const project = await updateProject(id, body)
    setProjects((current) => current.map((item) => (item.id === id ? { ...item, ...project } : item)))
    return project
  }, [])

  const remove = useCallback(
    async (id: string, disposition: "move_to_regular" | "delete_chats") => {
      await deleteProject(id, {
        chat_disposition: disposition,
        ...(disposition === "delete_chats" ? { confirm_delete_chats: true } : {}),
      })
      setProjects((current) => current.filter((item) => item.id !== id))
    },
    [],
  )

  return { projects, loading, refresh, create, update, remove }
}

export async function loadProjectDetail(id: string): Promise<ProjectDetail> {
  return getProject(id)
}
